/**
 * Basic geometric types for polygon processing
 */

export interface Point2D {
  x: number;
  y: number;
}

export interface Point3D {
  x: number;
  y: number;
  z: number;
}

/**
 * Recursive polygon structure supporting holes with infinite nesting
 *
 * - points: Boundary points
 * - isHole: Whether this ring is a hole (alternates at each nesting level)
 * - children: Optional array of child polygons (holes or islands)
 * - length: For array-like access (readonly)
 *
 * Alternating pattern:
 * - Level 0: isHole=false (main polygon)
 * - Level 1: isHole=true (holes in polygon)
 * - Level 2: isHole=false (islands in holes)
 * - Level 3: isHole=true (holes in islands)
 * - etc...
 */
export interface Polygon extends Iterable<Point2D> {
  points: Point2D[];
  isHole: boolean;
  children?: Polygon[];

  // Array-like interface for backward compatibility
  readonly length: number;
  [index: number]: Point2D;
}

/**
 * Helper to create a Polygon from Point2D array
 */
export function createPolygon(points: Point2D[], isHole = false): Polygon {
  const polygon = {
    points,
    isHole,
    children: undefined,
    get length() {
      return this.points.length;
    },
    [Symbol.iterator]() {
      return this.points[Symbol.iterator]();
    },
  } as Polygon;

  // Add array index access
  return new Proxy(polygon, {
    get(target, prop: string | symbol): unknown {
      if (typeof prop === 'string' && !isNaN(Number(prop))) {
        return target.points[Number(prop)];
      }
      return Reflect.get(target, prop);
    },
  });
}
